import React, { useState, useEffect } from "react";
import { FaPlus, FaTrash, FaTimes, FaMoneyBillWave, FaCalendarAlt, FaPen } from "react-icons/fa";
import API_URL from "../config";
import "./ApportsExploitant.css";

const API_APPORTS_URL = `${API_URL}/api/apports`;

export default function ApportsExploitant({ entrepriseId, entrepriseNom }) {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ montant: "", date: "", libelle: "" });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!entrepriseId) return;
    setLoading(true);
    fetch(`${API_APPORTS_URL}/${entrepriseId}`)
      .then((r) => r.json())
      .then((data) => {
        setItems(data);
        setLoading(false);
      })
      .catch((e) => {
        console.error("Erreur chargement apports:", e);
        setLoading(false);
      });
  }, [entrepriseId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((s) => ({ ...s, [name]: value }));
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.montant || !form.date || !form.libelle) return;

    try {
      const res = await fetch(API_APPORTS_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          entreprise_id: entrepriseId,
          montant: parseFloat(form.montant),
          date_apport: form.date,
          libelle: form.libelle,
        }),
      });
      if (!res.ok) throw new Error("Erreur serveur");
      const { id } = await res.json();
      setItems((prev) => [{ id, ...form, montant: parseFloat(form.montant), date_apport: form.date }, ...prev]);
      setForm({ montant: "", date: "", libelle: "" });
      setShowForm(false);
    } catch (err) {
      alert("Échec de l'ajout");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer cet apport ?")) return;
    try {
      const res = await fetch(`${API_APPORTS_URL}/${id}/${entrepriseId}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Erreur suppression");
      setItems((s) => s.filter((it) => it.id !== id));
    } catch (err) {
      alert("Échec de la suppression");
    }
  };

  // Fonction de formatage FCFA
  const formatFCFA = (valeur) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XOF',
      minimumFractionDigits: 0
    }).format(valeur).replace('XOF', 'FCFA');
  };

  return (
    <div className="container-fluid py-4 animate-fade-in">
      <div className="card shadow-sm border-0 bg-white rounded-4 overflow-hidden">
        {/* Header Section */}
        <div className="card-header bg-white py-4 px-4 border-bottom d-flex justify-content-between align-items-center flex-wrap gap-3">
          <div>
            <h2 className="h4 fw-bold text-dark mb-1">
              Apports de l'Exploitant
              {entrepriseNom && <span className="text-primary"> — {entrepriseNom}</span>}
            </h2>
            <p className="text-muted small mb-0">
              <span className="badge bg-light text-dark border">{items.length} enregistrements</span>
            </p>
          </div>
          <button className="btn btn-primary btn-lg rounded-pill shadow-sm px-4 d-flex align-items-center gap-2" onClick={() => setShowForm(true)}>
            <FaPlus /> <span className="d-none d-md-inline">Ajouter un Apport</span>
          </button>
        </div>

        {/* Table Section */}
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status"></div>
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-5 px-3">
              <div className="empty-state-icon mb-3" style={{fontSize: '3rem'}}>📂</div>
              <h5 className="text-muted">Aucun apport enregistré</h5>
              <p className="text-muted small">Cliquez sur le bouton "Ajouter" pour commencer.</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th className="ps-4 py-3">Montant</th>
                    <th className="py-3">Date</th>
                    <th className="py-3">Libellé</th>
                    <th className="text-end pe-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it) => (
                    <tr key={it.id} className="animate-row">
                      <td className="ps-4 fw-bold text-primary">
                        {formatFCFA(it.montant)}
                      </td>
                      <td>
                        <span className="text-muted"><FaCalendarAlt className="me-2" />{new Date(it.date_apport).toLocaleDateString()}</span>
                      </td>
                      <td className="text-dark">{it.libelle}</td>
                      <td className="text-end pe-4">
                        <button className="btn btn-outline-danger btn-sm rounded-circle border-0" onClick={() => handleDelete(it.id)}>
                          <FaTrash />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal Modernisé */}
      {showForm && (
        <div className="modal-custom-overlay d-flex align-items-center justify-content-center p-3">
          <div className="modal-custom-content bg-white rounded-4 shadow-lg animate-slide-up">
            <div className="modal-header border-0 pb-0 d-flex justify-content-between align-items-center p-4">
              <h5 className="fw-bold text-dark mb-0">Nouvel Apport</h5>
              <button className="btn btn-light rounded-circle" onClick={() => setShowForm(false)}>
                <FaTimes />
              </button>
            </div>
            <form onSubmit={handleAdd} className="p-4">
              <div className="mb-3">
                <label className="form-label fw-semibold">Montant (FCFA)</label>
                <div className="input-group">
                  <span className="input-group-text bg-white border-end-0"><FaMoneyBillWave /></span>
                  <input name="montant" type="number" className="form-control border-start-0" value={form.montant} onChange={handleChange} required placeholder="Ex: 50000" />
                </div>
              </div>
              <div className="mb-3">
                <label className="form-label fw-semibold">Date de l'apport</label>
                <div className="input-group">
                  <span className="input-group-text bg-white border-end-0"><FaCalendarAlt /></span>
                  <input name="date" type="date" className="form-control border-start-0" value={form.date} onChange={handleChange} required />
                </div>
              </div>
              <div className="mb-4">
                <label className="form-label fw-semibold">Libellé / Description</label>
                <div className="input-group">
                  <span className="input-group-text bg-white border-end-0"><FaPen /></span>
                  <input name="libelle" className="form-control border-start-0" value={form.libelle} onChange={handleChange} required placeholder="Ex: Apport personnel cash" />
                </div>
              </div>
              <div className="d-flex gap-3 mt-4">
                <button type="button" className="btn btn-light btn-lg w-100 fw-bold" onClick={() => setShowForm(false)}>Annuler</button>
                <button type="submit" className="btn btn-primary btn-lg w-100 fw-bold shadow">Enregistrer</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}